import React, { FC, Fragment, ChangeEvent, useState } from 'react';
import { CircularProgress, Grid, TextField, Typography, makeStyles } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useDebounce } from 'use-debounce';

import { useGroupSuggestQuery, GroupSuggestion, GroupFragment, GroupSuggestionFragment } from 'api';
import { GroupAvatar } from 'components';
import { useNotify } from 'hooks';

interface IProps {
  placeholder: string;
  value?: GroupSuggestionFragment;
  onChange: (group: GroupSuggestionFragment) => void;
  validationError: string | string[];
}

const useStyles = makeStyles(theme => ({
  avatar: {
    marginLeft: theme.spacing(2),
  },
  input: {
    '&::-webkit-input-placeholder': {
      opacity: 1,
    },
    '&::-moz-placeholder': {
      opacity: 1,
    },
    '&:-ms-input-placeholder': {
      opacity: 1,
    },
    '&::-ms-input-placeholder': {
      opacity: 1,
    },
  },
}));

const SuggestGroup: FC<IProps> = ({ placeholder, value, onChange, validationError, ...rest }) => {
  const classes = useStyles();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const [prefix] = useDebounce(inputValue, 250);
  const { data, isLoading } = useGroupSuggestQuery(
    { prefix },
    {
      onError: notify.queryError,
      enabled: !!prefix,
    }
  );

  const options = data?.suggest?.results ?? [];

  function handleChange(event: ChangeEvent, nextValue: GroupFragment) {
    onChange(nextValue);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
  }

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options as GroupSuggestionFragment[]}
      loading={isLoading}
      onChange={handleChange}
      getOptionSelected={option => {
        return option.title === value.title;
      }}
      getOptionLabel={option => option.title}
      disabled={!!value}
      disableClearable={!!value}
      renderOption={option => (
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <GroupAvatar group={option as GroupSuggestion} />
          </Grid>
          <Grid item>
            <Typography>{option.title}</Typography>
          </Grid>
        </Grid>
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={!!value && value.title !== '' ? value.title : placeholder}
          onChange={handleInputChange}
          error={!!validationError}
          helperText={validationError}
          InputProps={{
            ...params.InputProps,
            classes: { input: classes.input },
            startAdornment: !!value && <GroupAvatar className={classes.avatar} group={value} />,
            endAdornment: (
              <Fragment>
                {isLoading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
      {...rest}
    />
  );
};

export default SuggestGroup;
